import { useParams, Link, Navigate } from 'react-router-dom';
import SEO from '../components/SEO';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { blogPosts } from '../data/blog';

// ✅ Direct imports from your bimages folder
import imgWebDev      from '../data/bimages/Codeviora-blog-1.webp';
import imgAppDev      from '../data/bimages/Codeviora-blog-2.webp';
import imgDesign      from '../data/bimages/Codeviora-blog-3.webp';
import imgSEO         from '../data/bimages/Codeviora-blog-4.webp';
import imgMarketing   from '../data/bimages/Codeviora-blog-5.webp';
import imgAI          from '../data/bimages/Codeviora-blog-6.webp';

const BLOG_IMAGES = {
  'Web Development'        : imgWebDev,
  'App Development'        : imgAppDev,
  'Web Design'             : imgDesign,
  'SEO'                    : imgSEO,
  'Digital Marketing'      : imgMarketing,
  'Artificial Intelligence': imgAI,
};

const FALLBACK_IMAGE = imgWebDev;

// Generates a consistent pseudo view-count per post (based on slug)
function getViewCount(slug) {
  let hash = 0;
  for (let i = 0; i < slug.length; i++) {
    hash = (hash << 5) - hash + slug.charCodeAt(i);
    hash |= 0;
  }
  const views = 800 + (Math.abs(hash) % 4200); // range: 800 – 5000
  return views.toLocaleString();
}

// ── Share Button Component ────────────────────────────────
function ShareButtons({ title, excerpt }) {
  const url = encodeURIComponent(window.location.href);
  const text = encodeURIComponent(`${title} — ${excerpt}`);

  const shares = [
    {
      name: 'LinkedIn',
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
      color: 'hover:bg-[#0077b5] hover:border-[#0077b5]',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
        </svg>
      ),
    },
    {
      name: 'Twitter / X',
      href: `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
      color: 'hover:bg-black hover:border-black',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.748l7.73-8.835L1.254 2.25H8.08l4.259 5.631 5.905-5.631zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
      ),
    },
    {
      name: 'Facebook',
      href: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      color: 'hover:bg-[#1877f2] hover:border-[#1877f2]',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      ),
    },
    {
      name: 'WhatsApp',
      href: `https://wa.me/?text=${text}%20${url}`,
      color: 'hover:bg-[#25d366] hover:border-[#25d366]',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      ),
    },
  ];

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      alert('Link copied to clipboard!');
    });
  };

  return (
    <div className="flex flex-wrap items-center gap-3 mt-10 pt-8 border-t border-black/10 dark:border-white/10 reveal">
      <span className="text-sm font-medium text-black/40 dark:text-white/40 mr-1">Share:</span>
      {shares.map(({ name, href, color, icon }) => (
        <a
          key={name}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Share on ${name}`}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl border border-black/10 dark:border-white/10 text-black/50 dark:text-white/50 hover:text-white text-xs font-medium transition-all duration-200 ${color}`}
        >
          {icon}
          <span className="hidden sm:inline">{name}</span>
        </a>
      ))}
      {/* Copy Link */}
      <button
        onClick={copyLink}
        aria-label="Copy link"
        className="flex items-center gap-2 px-4 py-2 rounded-xl border border-black/10 dark:border-white/10 text-black/50 dark:text-white/50 hover:bg-black hover:border-black hover:text-white text-xs font-medium transition-all duration-200"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
          <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
        </svg>
        <span className="hidden sm:inline">Copy Link</span>
      </button>
    </div>
  );
}

export default function BlogDetail() {
  useScrollReveal();
  const { slug } = useParams();
  const post = blogPosts.find(p => p.slug === slug);
  if (!post) return <Navigate to="/blog" replace />;

  // Related: same category pehle, phir baaki — current post exclude
  const related = [
    ...blogPosts.filter(p => p.slug !== slug && p.category === post.category),
    ...blogPosts.filter(p => p.slug !== slug && p.category !== post.category),
  ].slice(0, 3);

  return (
    <>
      <SEO
        title={post.title}
        description={post.excerpt}
        canonical={`/blog/${post.slug}`}
        ogType="article"
      />

      {/* Back */}
      <div className="bg-pure-black border-white/10 pt-16">
        <div className="container-max py-4">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-sm text-white/40 hover:text-white transition-colors font-medium"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M10 4L6 8l4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Back to Blog
          </Link>
        </div>
      </div>

      {/* Hero image */}
      <div className="bg-pure-black pb-16">
        <div className="container-max">
          <div className="overflow-hidden rounded-2xl">
            <img
              src={BLOG_IMAGES[post.category] || FALLBACK_IMAGE}
              alt={post.title}
              className="img-reveal w-full h-full transition-transform duration-500"
              loading="eager"
            />
          </div>
        </div>
      </div>

      {/* Header */}
      <header className="bg-grid-black pb-16 pt-10">
        <div className="container-max max-w-3xl animate-fade-up">
          <div className="flex flex-wrap items-center gap-3 mb-5">
            <span className="text-xs font-medium text-white bg-white/10 border border-white/10 px-3 py-1.5 rounded-full">
              {post.category}
            </span>
            <span className="text-xs text-white/30">{post.readTime}</span>
            <span className="text-xs text-white/30">{post.date}</span>
            {/* <span className="text-xs text-white/30 flex items-center gap-1">
              <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
                <path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.3"/>
              </svg>
              {getViewCount(post.slug)} views
            </span>*/}
          </div>
          <h1
            className="text-3xl md:text-5xl text-white leading-tight mb-5"
            style={{ fontFamily: 'Anton,sans-serif', fontWeight: 500 }}
          >
            {post.title}
          </h1>
          <p className="text-lg text-white/50 leading-relaxed mb-8">{post.excerpt}</p>
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-white dark:bg-[#111] flex items-center justify-center text-black dark:text-white font-black text-sm">
              CV
            </div>
            <div>
              <p className="text-sm font-medium text-white">{post.author.name}</p>
              <p className="text-xs text-white/30">{post.author.role}</p>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <article className="bg-grid-white py-14 md:py-20">
        <div className="container-max max-w-3xl">
          <div className="prose-custom reveal" dangerouslySetInnerHTML={{ __html: post.content }} />

          {/* Tags */}
          <div className="flex flex-wrap gap-2 mt-12 pt-10 border-t border-black/10 dark:border-white/10 reveal">
            {post.tags.map(tag => (
              <span
                key={tag}
                className="text-xs font-medium text-black/50 dark:text-white/50 bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10 px-3 py-1.5 rounded-full"
              >
                #{tag}
              </span>
            ))}
          </div>

          {/* ✅ Share Buttons */}
          <ShareButtons title={post.title} excerpt={post.excerpt} />

          {/* CTA inside blog */}
          <div className="mt-12 bg-black rounded-2xl p-8 md:p-10 text-center reveal">
            <h3
              className="text-2xl text-white mb-2"
              style={{ fontFamily: 'Anton,sans-serif', fontWeight: 500 }}
            >
              NEED HELP WITH YOUR PROJECT?
            </h3>
            <p className="text-white/50 text-sm mb-5">Talk to our team. Free consultation, no commitment.</p>
            <Link to="/contact" className="btn-primary-inv inline-flex">
              Get Free Quote →
            </Link>
          </div>
        </div>
      </article>

      {/* Related */}
      <section className="bg-grid-black py-16 border-t border-white/10">
        <div className="container-max">
          <h2
            className="text-2xl text-white mb-8 reveal"
            style={{ fontFamily: 'Anton,sans-serif', fontWeight: 500 }}
          >
            MORE FROM THE BLOG
          </h2>

          {/* Desktop Grid */}
          <div className="hidden md:grid md:grid-cols-3 gap-6">
            {related.map(({ slug: rslug, title, category, readTime, date }, i) => (
              <Link
                key={rslug}
                to={`/blog/${rslug}`}
                className="reveal card-black group overflow-hidden flex flex-col"
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <div className="h-36 overflow-hidden rounded-t-2xl">
                  <img
                    src={BLOG_IMAGES[category] || FALLBACK_IMAGE}
                    alt={title}
                    className="img-reveal w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-medium text-white/50">{category}</span>
                    <span className="text-xs text-white/20">{readTime}</span>
                  </div>
                  <h3
                    className="text-white text-sm leading-snug group-hover:text-white/60 transition-colors flex-1"
                    style={{ fontFamily: 'Anton,sans-serif', fontWeight: 500 }}
                  >
                    {title}
                  </h3>
                  <p className="text-xs text-white/20 mt-4">{date}</p>
                </div>
              </Link>
            ))}
          </div>

          {/* Mobile Swipe Carousel */}
          <div className="md:hidden">
            <div
              className="flex gap-4 overflow-x-auto scrollbar-none snap-x snap-mandatory pb-4"
              style={{ scrollbarWidth: 'none' }}
            >
              {related.map(({ slug: rslug, title, category, readTime, date }) => (
                <Link
                  key={rslug}
                  to={`/blog/${rslug}`}
                  className="flex-shrink-0 w-64 card-black group overflow-hidden flex flex-col snap-start"
                >
                  <div className="h-36 overflow-hidden rounded-t-2xl">
                    <img
                      src={BLOG_IMAGES[category] || FALLBACK_IMAGE}
                      alt={title}
                      className="img-reveal w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-medium text-white/50">{category}</span>
                      <span className="text-xs text-white/20">{readTime}</span>
                    </div>
                    <h3
                      className="text-white text-sm leading-snug flex-1"
                      style={{ fontFamily: 'Anton,sans-serif', fontWeight: 500 }}
                    >
                      {title}
                    </h3>
                    <p className="text-xs text-white/20 mt-4">{date}</p>
                  </div>
                </Link>
              ))}
            </div>
            <p className="text-center text-xs text-white/30 mt-3">← Swipe to see more →</p>
          </div>
        </div>
      </section>
    </>
  );
}
